import { 
  FormControl, 
  FormLabel,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Checkbox,
  Text,
  Box,
  Flex,
  HStack,
  IconButton,
  Icon,
  Input,
} from "@chakra-ui/react";
import React , { useMemo, useState, useEffect } from "react";
import { Stack } from "@chakra-ui/react";
import { ChevronDownIcon, CloseIcon, EditIcon, CheckIcon } from "@chakra-ui/icons";
import { Button } from "@chakra-ui/react";
import { ColumnHashProto } from "build-ts/data";
import { MetricProto, ObservationColumnProto } from "build-ts/metadata";
import { BenchmarkEntityProto } from "build-ts/knowledege_graph";
import { AnalysisColumnType, ColumnType } from "build-ts/enums";
import { AvailableNode, PropagationItem, StepInterface } from "./Interface";
import { useAnalysisPlanContext } from "context/batch/AnalysisPlanContext";
import PropagationModal from "containers/analysis-plan/PropagationModal";
import { TbFilterBolt } from "react-icons/tb";


interface AnalysisProps {
  currentNode: StepInterface;
  analysisColumns: ColumnHashProto[];
  setAnalysisColumns: React.Dispatch<React.SetStateAction<ColumnHashProto[]>>;
  selectedColumns: ObservationColumnProto[];
  selectedMetrics: MetricProto[];
  selectedBenchmarks: BenchmarkEntityProto[];
  benchmarkColumnHashProto: ColumnHashProto[];
}

const Analysis : React.FC<AnalysisProps> = ({ 
  currentNode,
  analysisColumns, 
  setAnalysisColumns,
  selectedColumns,
  selectedMetrics,
  selectedBenchmarks,
  benchmarkColumnHashProto
}) => {
  // Get context functions
  const { nodeMap, updateNode } = useAnalysisPlanContext();

  const [selectedAnalysisType, setSelectedAnalysisType] = useState<AnalysisColumnType | null>(null);
  const [selectedColumnItems, setSelectedColumnItems] = useState<string[]>([]);
  const [combinationMap, setCombinationMap] = useState<Map<string, ColumnHashProto>>(new Map());
  
  // State for inline editing
  const [editingColumnName, setEditingColumnName] = useState<string>("");
  const [editingAnalysisIndex, setEditingAnalysisIndex] = useState<number>(-1);
  
  // State for propagation modal
  const [propagationModal, setPropagationModal] = useState<{
    isOpen: boolean;
    item: PropagationItem | null;
    availableNodes: AvailableNode[];
    shouldOverwrite: boolean;
  }>({
    isOpen: false,
    item: null,
    availableNodes: [],
    shouldOverwrite: true
  });

  // Column type options
  const allColumnTypeOptions = [
    { value: AnalysisColumnType.VARIANCE, label: "Variance" },
    { value: AnalysisColumnType.VARIANCE_PERCENTAGE, label: "Variance Percentage" },
    { value: AnalysisColumnType.NORMALISED_TARGET, label: "Normalised Target" },
    { value: AnalysisColumnType.ACCOMPLISH_PERCENTAGE, label: "Accomplish Percentage" },
    { value: AnalysisColumnType.COMPOSITION_PERCENTAGE, label: "Composition Percentage" },
    { value: AnalysisColumnType.RANK, label: "Rank" },
  ];

  // Initialize maps on component mount
  useEffect(() => {
    // Create combination map
    const newCombinationMap = new Map<string, ColumnHashProto>();
    benchmarkColumnHashProto.forEach(hash => {
      if (hash.actuals_id && hash.benchmark_id) {
        const column = selectedColumns.find(col => col.column_id === hash.actuals_id);
        const metric = selectedMetrics.find(metric => metric.header?.id === hash.actuals_id);
        const actualsName = column?.kg_column_header?.name || metric?.header?.name || "Unknown";
        const benchmark = selectedBenchmarks.find(b => b.header?.id === hash.benchmark_id);
        const benchmarkName = benchmark?.header?.name || "Unknown Benchmark";
        const combinationName = `${actualsName} (${benchmarkName})`;
        newCombinationMap.set(combinationName, hash);
      }
    });
    setCombinationMap(newCombinationMap);
  }, [benchmarkColumnHashProto]);

  // Handle analysis column propagation click
  const handlePropagateAnalysisClick = (analysisColumn: ColumnHashProto, currentNodeId: string) => {
    // Get all nodes from the nodeMap, excluding the current node
    const allNodeIds = Array.from(nodeMap.keys()).filter(id => id !== currentNodeId);
    const allNodes = allNodeIds.map((id) => {
      const node = nodeMap.get(id);
      return {
        id,
        name: node?.label || `Node ${id}`,
        selected: true,
      };
    });
    
    setPropagationModal({
      isOpen: true,
      item: {
        type: "analysis", 
        analysisColumn: analysisColumn
      } as any,
      availableNodes: allNodes,
      shouldOverwrite: true
    });
  };

  // Handle propagation modal node toggle
  const handlePropagationNodeToggle = (nodeId: string) => {
    setPropagationModal(prev => ({
      ...prev,
      availableNodes: prev.availableNodes.map(node => 
        node.id === nodeId 
          ? { ...node, selected: !node.selected }
          : node
      )
    }));
  };

  // Handle propagation modal overwrite toggle
  const handlePropagationOverwriteToggle = (shouldOverwrite: boolean) => {
    setPropagationModal(prev => ({
      ...prev,
      shouldOverwrite
    }));
  };

  // Function to confirm analysis column propagation to selected descendants
  const handleConfirmPropagation = () => {
    if (!propagationModal.item || !propagationModal.item.analysisColumn || !currentNode) return;

    const analysisColumnToPropagate = propagationModal.item.analysisColumn;

    // Only use selected descendants for propagation
    const selectedDescendantIds = propagationModal.availableNodes
      .filter(node => node.selected)
      .map(node => node.id);

    selectedDescendantIds.forEach(descendantId => {
      const descendantNode = nodeMap?.get(descendantId);
      if (descendantNode) {
        // Handle analysis column propagation
        if (propagationModal.shouldOverwrite) {
          // Remove existing analysis column with same actuals_id, benchmark_id, and analysis_column_type
          descendantNode.analysis_columns = descendantNode.analysis_columns?.filter(
            col => !(col.actuals_id === analysisColumnToPropagate.actuals_id &&
                    col.benchmark_id === analysisColumnToPropagate.benchmark_id &&
                    col.analysis_column_type === analysisColumnToPropagate.analysis_column_type)
          ) || [];
        }
        
        // Check if target node has the required actuals_id and benchmark_id
        const targetColumnIds = new Set<string>();
        
        // Collect all available column IDs in the target node
        descendantNode.selected_metrics?.forEach(metric => {
          if (metric.header?.id) targetColumnIds.add(metric.header.id);
        });
        descendantNode.selected_columns?.forEach(column => {
          if (column.column_id && 
              column.kg_column_header?.column_type !== ColumnType.ATTRIBUTE &&
              column.kg_column_header?.column_type !== ColumnType.TEMPORAL) {
            targetColumnIds.add(column.column_id);
          }
        });

        const targetBenchmarkIds = new Set<string>();
        descendantNode.selected_benchmarks?.forEach(benchmark => {
          if (benchmark.header?.id) targetBenchmarkIds.add(benchmark.header.id);
        });
        
        // Only add if the target node has the required actuals and benchmark
        if (analysisColumnToPropagate.actuals_id && 
            targetColumnIds.has(analysisColumnToPropagate.actuals_id) &&
            analysisColumnToPropagate.benchmark_id &&
            targetBenchmarkIds.has(analysisColumnToPropagate.benchmark_id)) {
          
          // Check if this exact analysis column already exists
          const exists = descendantNode.analysis_columns?.some(existing =>
            existing.actuals_id === analysisColumnToPropagate.actuals_id &&
            existing.benchmark_id === analysisColumnToPropagate.benchmark_id &&
            existing.analysis_column_type === analysisColumnToPropagate.analysis_column_type
          );
          
          if (!exists) {
            const c_hash = ColumnHashProto.create({
              actuals_id: analysisColumnToPropagate.actuals_id,
              benchmark_id: analysisColumnToPropagate.benchmark_id,
              analysis_column_type: analysisColumnToPropagate.analysis_column_type,
              is_material: analysisColumnToPropagate.is_material || false,
            } as ColumnHashProto);
            if ((analysisColumnToPropagate as any).column_name) {
              c_hash.column_name = (analysisColumnToPropagate as any).column_name;
            }
            descendantNode.analysis_columns?.push(c_hash);
          }
        }
        
        updateNode(descendantNode);
      }
    });

    // Close modal and reset state
    setPropagationModal({
      isOpen: false,
      item: null,
      availableNodes: [],
      shouldOverwrite: true
    });
  };

  // Close propagation modal
  const handleClosePropagation = () => {
    setPropagationModal({
      isOpen: false,
      item: null,
      availableNodes: [],
      shouldOverwrite: true
    });
  };

  // Check if selected analysis type is Contribution% or Rank
  const isContributionOrRank = selectedAnalysisType === AnalysisColumnType.COMPOSITION_PERCENTAGE || 
                              selectedAnalysisType === AnalysisColumnType.RANK;

  // Generate column options based on analysis type
  const columnOptions = useMemo(() => {
    if (!selectedAnalysisType) return [];

    const options: Array<{id: string, name: string, hash?: ColumnHashProto}> = [];

    if (isContributionOrRank) {
      if(benchmarkColumnHashProto.length === 0){
        // If no benchmark is selected, show actuals only
        selectedMetrics.forEach(metric => {
          options.push({ id: metric.header?.id || "", name: metric.header?.name || "" });
        });
      } else {
        // For Contribution% or Rank: Show all the actuals + combinations
        selectedMetrics.forEach(metric => {
          options.push({ id: metric.header?.id || "", name: metric.header?.name || "" });
        });
        combinationMap.forEach((hash, combinationName) => {
          options.push({ id: combinationName, name: combinationName, hash: hash });
        });
    }
    } else {
      // For other analysis types: Only show combinations
      combinationMap.forEach((hash, combinationName) => {
        options.push({ id: combinationName, name: combinationName, hash: hash });
      });
    }

    console.log("options", options);
    return options;
  }, [selectedAnalysisType, isContributionOrRank]);

  // Handler for analysis type selection
  const handleAnalysisTypeSelect = (analysisType: AnalysisColumnType) => {
    setSelectedAnalysisType(analysisType);
    // Reset selected column items when analysis type changes
    setSelectedColumnItems([]);
  };

  // Handler for column item selection (multiselect)
  const handleColumnItemToggle = (itemId: string) => {
    setSelectedColumnItems(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });
  };

  // Get benchmark name from benchmarkColumnHashProto
  const getBenchmarkName = (columnHash: ColumnHashProto | undefined) => {
    if (!columnHash) {
      return "Unnamed Benchmark";
    }
    const benchmark = selectedBenchmarks.find(b => b.header?.id === columnHash.benchmark_id);
    return benchmark?.header?.name;
  };
  
  // function to convert to convert name from column hash proto
  const getActualsName = (columnHash: ColumnHashProto) => {
    const column = selectedColumns.find(col => col.column_id === columnHash.actuals_id);
    const columnName = column?.kg_column_header?.name || "";

    const metric = selectedMetrics.find(metric => metric.header?.id === columnHash.actuals_id);
    const metricName = metric?.header?.name || "";

    return columnName.length > 0 ? columnName : metricName.length > 0 ? metricName : "Unnamed Column";
  };

  // function to handle add analysis
  const handleAddAnalysis = () => {
    if (!selectedColumnItems.length || !selectedAnalysisType) return;
  
    const newAnalysisColumns : ColumnHashProto[] = [];
    
    selectedColumnItems.forEach(itemId => {
      const option = columnOptions.find(opt => opt.id === itemId);
      if (!option) return;

      if (option.hash) {
        // This is a combination (with benchmark)
        newAnalysisColumns.push(ColumnHashProto.create({
          actuals_id: option.hash.actuals_id,
          benchmark_id: option.hash.benchmark_id,
          analysis_column_type: selectedAnalysisType,
        }));
      } else {
        // This is an individual actuals (without benchmark)
        newAnalysisColumns.push(ColumnHashProto.create({
          actuals_id: itemId,
          analysis_column_type: selectedAnalysisType,
        }));
      }
    });

    // Add to existing analysis columns
    setAnalysisColumns([...analysisColumns, ...newAnalysisColumns]);

    // reset the selected items
    setSelectedColumnItems([]);
    setSelectedAnalysisType(null);
  };

  // function to remove analysis column
  const handleRemoveAnalysis = (index: number) => {
    const updatedColumns = analysisColumns.filter((_, i) => i !== index);
    setAnalysisColumns(updatedColumns);
  };
  
  // function to start editing an analysis column
  const handleEditAnalysisColumn = (index: number, currentName: string) => {
    setEditingAnalysisIndex(index);
    setEditingColumnName(currentName);
  };
  
  // function to handle saving edits
  const handleSaveEdit = () => {
    // Save the edited column name
    const updatedColumns = [...analysisColumns];
    (updatedColumns[editingAnalysisIndex] as any).column_name = editingColumnName;
    setAnalysisColumns(updatedColumns);
    handleCancelEdit();
  };

  const handleCancelEdit = () => {
    // Reset editing state
    setEditingAnalysisIndex(-1);
    setEditingColumnName("");
  };

  // function to get analysis display text
  const getAnalysisDisplayText = (column: ColumnHashProto) => {
    if ((column as any).column_name) {
      return (column as any).column_name;
    }
    const actualsName = getActualsName(column);
    const benchmarkName = getBenchmarkName(column);
    const analysisTypeLabel = allColumnTypeOptions.find(opt => opt.value === column.analysis_column_type)?.label;
    return `${actualsName} (${benchmarkName || "No Benchmark"}) ${analysisTypeLabel}`;
  };

  return (
    <Stack spacing={4}>
      {/* 1st Dropdown: Analysis Type (Single Select) */}
      <FormControl>
        <FormLabel fontWeight="semibold" color="purple.700" ml={1}>Select Analysis Type</FormLabel>
        <Menu closeOnSelect={true}>
          <MenuButton
            as={Button}
            rightIcon={<ChevronDownIcon />}
            w="100%"
            textAlign="left"
            variant="outline"
            colorScheme="purple"
            //isDisabled={benchmarkColumnHashProto.length === 0}
          >
            {selectedAnalysisType 
              ? allColumnTypeOptions.find(opt => opt.value === selectedAnalysisType)?.label 
              : benchmarkColumnHashProto.length === 0 
                ? "No columns available"
                : "Select Analysis Type"}
          </MenuButton>
          <Portal>
            <MenuList maxH="200px" overflowY="auto" zIndex={1500}>
              {allColumnTypeOptions.map((option, index) => (
                <MenuItem
                  key={index}
                  _hover={{ bg: "purple.50" }}
                  onClick={() => handleAnalysisTypeSelect(option.value)}
                >
                  {option.label}
                </MenuItem>
              ))}
            </MenuList>
          </Portal>
        </Menu>
      </FormControl>

      {/* 2nd Dropdown: Column Selection (Multi-select) */}
      <FormControl>
        <FormLabel fontWeight="semibold" color="purple.700" ml={1}>Select Columns</FormLabel>
        <Menu closeOnSelect={false}>
          <MenuButton
            as={Button}
            rightIcon={<ChevronDownIcon />}
            w="100%"
            textAlign="left"
            variant="outline"
            colorScheme="purple"
            isDisabled={!selectedAnalysisType}
          >
            {!selectedAnalysisType
              ? "Select Analysis Type first"
              : columnOptions.length === 0
                ? "No columns available"
                : selectedColumnItems.length > 0 
                  ? `${selectedColumnItems.length} column${selectedColumnItems.length > 1 ? "s" : ""} selected`
                  : "Select Columns"}
          </MenuButton>
          <Portal>
            <MenuList maxH="200px" overflowY="auto" zIndex={1500}>
              {columnOptions.map((option, index) => (
                <MenuItem
                  key={index}
                  _hover={{ bg: "purple.50" }}
                  onClick={() => handleColumnItemToggle(option.id)}
                  closeOnSelect={false}
                >
                  <Checkbox
                    isChecked={selectedColumnItems.includes(option.id)}
                    mr={2}
                    colorScheme="purple"
                    pointerEvents="none"
                  />
                  <Text fontWeight="600">{option.name}</Text>
                </MenuItem>
              ))}
            </MenuList>
          </Portal>
        </Menu>
      </FormControl>

      {/* Add Analysis Button */}
      <FormControl>
        <Button
          colorScheme="purple"
          onClick={handleAddAnalysis}
          width="100%"
          bg="purple.700"
          fontWeight="700"
          _hover={{ bg: "purple.600" }}
          isDisabled={!selectedAnalysisType || selectedColumnItems.length === 0}
        >
          Add Analysis
        </Button>
      </FormControl>

      {/* Display selected analysis columns */}
      {analysisColumns.length > 0 && (
        <Box mt={4}>
          <Text fontWeight="medium" mb={2} color="purple.700">
            Selected Analysis Columns:
          </Text>
          <Stack spacing={2}>
            {analysisColumns.map((column, index) => (
              <Box
                key={index}
                borderWidth="1px"
                borderRadius="md"
                p={3}
                backgroundColor="gray.50"
                borderColor="purple.700"
              >
                <Flex justifyContent="space-between" alignItems="center">
                  <Box flex="1" mr={2}>
                    {/* If this column is being edited, show edit form */}
                    {editingAnalysisIndex === index &&(
                      <>
                      <Input
                          value={editingColumnName}
                          onChange={(e) => setEditingColumnName(e.target.value)}
                          placeholder="Enter column name"
                          size="sm"
                          bg="white"
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              handleSaveEdit();
                            } else if (e.key === "Escape") {
                              handleCancelEdit();
                            }
                          }}
                      />
                      <HStack spacing={1}>
                        <IconButton
                          aria-label="Save analysis column name"
                          icon={<CheckIcon />}
                          size="xs"
                          onClick={handleSaveEdit}
                          variant="ghost"
                          colorScheme="green"
                          _hover={{ bg: "green.100" }}
                        />
                        <IconButton
                          aria-label="Cancel edit"
                          icon={<CloseIcon />}
                          size="xs"
                          onClick={handleCancelEdit}
                          variant="ghost"
                        />
                      </HStack>
                      </>
                    )}
                    <Text
                      fontSize="sm"
                      fontWeight="bold"
                      color="purple.800"
                      noOfLines={1}
                      title={getAnalysisDisplayText(column)}
                    >
                      {getAnalysisDisplayText(column)}
                    </Text>
                    <Flex fontSize="xs" color="gray.600" mt={0.5}>
                      <Box>
                      {column.analysis_column_type === AnalysisColumnType.RANK || column.analysis_column_type === AnalysisColumnType.COMPOSITION_PERCENTAGE ? (
                        <>
                        <FormLabel mb={0} fontSize="xs" color="gray.600">Column</FormLabel>
                        <Box mb={1}>
                          <Box as="span" bg="blue.100" color="blue.800" px={2} py={0.5} borderRadius="md" fontSize="sm">
                            {                            
                              (() => {
                                const bm_c_hash = benchmarkColumnHashProto.find(bc => 
                                bc.benchmark_id === column.benchmark_id && 
                                bc.actuals_id === column.actuals_id
                                );
                                if (!bm_c_hash) {
                                  return getActualsName(column);
                                }
                                return bm_c_hash?.column_name || (getActualsName(column) + " (" + getBenchmarkName(column) + ")");
                              })()
                            }
                          </Box>
                        </Box>
                        <FormLabel mb={0} fontSize="xs" color="gray.600">Analysis Type</FormLabel>
                        <Box>
                          <Box as="span" bg="blue.100" color="blue.800" px={2} py={0.5} borderRadius="md" fontSize="sm">
                          {allColumnTypeOptions.find(opt => opt.value === (column.analysis_column_type as any))?.label || String(column.analysis_column_type)}
                          </Box>
                        </Box>
                        </>
                      ) : (
                        <>
                        <FormLabel mb={0} fontSize="xs" color="gray.600">Actuals</FormLabel>
                        <Box mb={1}>
                          <Box as="span" bg="blue.100" color="blue.800" px={2} py={0.5} borderRadius="md" fontSize="sm">
                          {getActualsName(column)}
                          </Box>
                        </Box>
                        <FormLabel mb={0} fontSize="xs" color="gray.600">Benchmark</FormLabel>
                        <Box mb={1}>
                          <Box as="span" bg="blue.100" color="blue.800" px={2} py={0.5} borderRadius="md" fontSize="sm">
                          {getBenchmarkName(column)}
                          </Box>
                        </Box>
                        <FormLabel mb={0} fontSize="xs" color="gray.600">Analysis Type</FormLabel>
                        <Box>
                          <Box as="span" bg="blue.100" color="blue.800" px={2} py={0.5} borderRadius="md" fontSize="sm">
                          {allColumnTypeOptions.find(opt => opt.value === (column.analysis_column_type as any))?.label || String(column.analysis_column_type)}
                          </Box>
                        </Box>
                        </>
                      )}
                      </Box>
                    </Flex>
                    </Box>
                    <HStack spacing={1}>
                      <IconButton
                        aria-label="Edit analysis column name"
                        icon={<Icon as={EditIcon} boxSize={4} color="purple.700" />}
                        size="xs"
                        onClick={() => handleEditAnalysisColumn(index, getAnalysisDisplayText(column))}
                        variant="ghost"
                        colorScheme="purple"
                        _hover={{ bg: "purple.100" }}
                      />
                      <IconButton
                        aria-label="Propagate analysis column"
                        icon={<Icon as={TbFilterBolt} boxSize={4} color="purple.700" />}
                        size="xs"
                        onClick={() => handlePropagateAnalysisClick(column, currentNode?.id || "")}
                        variant="ghost"
                        colorScheme="purple"
                        _hover={{ bg: "purple.100" }}
                      />
                      <IconButton
                        aria-label="Remove analysis"
                        icon={<Icon as={CloseIcon} boxSize={3} _hover={{boxSize:4}} />}
                        size="xs"
                        onClick={() => handleRemoveAnalysis(index)}
                        variant="ghost"
                        colorScheme="red"
                      />
                    </HStack>
                  </Flex>
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      {/* Propagation Modal */}
      <PropagationModal
        isOpen={propagationModal.isOpen}
        onClose={handleClosePropagation}
        item={propagationModal.item}
        availableNodes={propagationModal.availableNodes}
        shouldOverwrite={propagationModal.shouldOverwrite}
        onToggleOverwrite={handlePropagationOverwriteToggle}
        onToggleNode={handlePropagationNodeToggle}
        onConfirm={handleConfirmPropagation}
      />
    </Stack>
  );
};

export default Analysis;